import * as Y from 'yjs';
import { generateKeyBetween } from 'fractional-indexing';
import { normalizeAngle } from '@os/engine';
import { newId } from './ids';
import { normalizeObjects } from './normalize';
import { alignDeltas, ARRANGE_MARGIN, arrangeInside, arrangeLayout, clampCrop, cropBox, distributeDeltas, fitSectionBox, type AlignMode, type ArrangeMode, type Crop } from '@os/engine';
import { aabb, descendantsOf, isEffectivelyLocked, topLevel, type SceneGraph } from './graph';
import { LOCAL_ORIGIN, REPAIR_ORIGIN } from './origin';
import { fillFragment, plainToRich } from './rich';
import {
  CAPS, CONTAINER_TYPES, parseObject, ROUTINGS, SCHEMA_VERSION, SHAPES, TABLE_MAX_COLS, TABLE_MAX_ROWS, TABLE_ROW_H, TASK_MAX_INDENT, TASK_MAX_ITEMS, TASK_MAX_TEXT,
  TASK_MAX_TITLE, taskListHeight, type ConnectorEnd, type ObjectData,
} from './schema';
import { resolveEnd } from './connectors';
import { boardCounts, overLimit, type LimitKind } from './limits';
import { isBoardMetaPatch, type BoardMeta } from './meta';
import { PAGE_LIMIT, PAGE_MAIN, PAGE_NAME_MAX, readPages } from './pages';
import { applyTextDiff, TABLE_COL_W, TABLE_MAX_COL_W, TABLE_MIN_COL_W } from './table';
import { posAt, repairTaskOrder, sortedTaskEntries } from './tasks';
import { isTextType, writeObject } from './ytypes';
import { stylePatch, type StyleClip } from './style-clip';

type DistributiveOmit<T, K extends keyof never> = T extends unknown ? Omit<T, K> : never;
export type NewObject = DistributiveOmit<ObjectData, 'id' | 'z'>;
export type Box = { x: number; y: number; w: number; h: number };
export type Point = { x: number; y: number };
export type LinkPreviewPatch = Partial<{
  title: string;
  description: string;
  siteName: string;
  status: 'pending' | 'ready' | 'failed';
  embeddable: boolean | null;
  thumbAssetId: string | null;
}>;
export type PropsPatch = Partial<{
  locked: boolean;
  color: string;
  fontSize: number;
  shape: (typeof SHAPES)[number];
  /** Connectors only. */
  routing: (typeof ROUTINGS)[number];
  startCap: (typeof CAPS)[number];
  endCap: (typeof CAPS)[number];
  /** Midpoint offset in chord lengths, clamped to [−2, 2]. */
  bend: number;
  /** Text on the line, at most 200 characters. */
  label: string;
  /** Sections only. */
  name: string;
  fill: string;
  clip: boolean;
  collapsed: boolean;
  /** Images only: the alternative text, at most 500 characters (P5f). */
  alt: string;
  /** Task lists only: shorter rows (P16-5). */
  compact: boolean;
  /** Page links only (P14): the page it shows and opens. */
  targetPageId: string;
}>;

/**
 * Geometry arguments are in WORLD coordinates (documents store children relative to their parent; commands convert).
 */
export interface Commands {
  /** `data` is document data: `x, y` are relative to `data.parentId` (world when it is null). */
  createObject(data: NewObject, opts?: { above?: string }): string;
  /** Moves the given objects, skipping any whose ancestor is also given (it moves with it). */
  moveObjects(ids: string[], dx: number, dy: number): void;
  resizeObject(id: string, box: Box): void;
  /** Sections, groups and task lists never rotate (skipped). */
  rotateObjects(ids: string[], deltaDeg: number, pivot?: Point): void;
  setStyle(ids: string[], patch: Record<string, unknown>): void;
  /** Applies a copied style to each object, what its type supports (one transaction). */
  pasteStyle(ids: string[], clip: StyleClip): void;
  /**
   * Top-level properties: `locked` on any object, `color` on stickies, `fontSize` on texts, `shape` on shapes, `name`,
   * `fill`, `clip` and `collapsed` on sections (others skipped).
   */
  setProps(ids: string[], patch: PropsPatch): void;
  /** Replaces the content of a text, sticky or shape with one paragraph per line (other types are skipped). */
  setText(id: string, text: string): void;
  /**
   * The rich-text fragment of a text type, created from its plain text when a pre-v2 object has none (REPAIR_ORIGIN:
   * not an undo step). Null for other types and when read-only.
   */
  ensureContent(id: string): Y.XmlFragment | null;
  reorder(ids: string[], where: 'front' | 'back' | 'forward' | 'backward'): void;
  /** Copies the given objects with their descendants; returns the copies of the top-level ones. */
  duplicate(ids: string[], offset: Point): string[];
  /** Deletes the given objects and their descendants; connector ends attached to them stay where they were, free. */
  deleteObjects(ids: string[]): void;
  setConnectorEnd(id: string, which: 'start' | 'end', end: ConnectorEnd): void;
  /**
   * Moves objects under `parentId` (a section or a group, or null for the board) keeping their world position, above
   * their new siblings. Skips connectors, and any move that would make an object its own ancestor.
   */
  reparent(ids: string[], parentId: string | null): void;
  /**
   * Inserts a batch in one transaction (paste, templates). `parentId` and connector `objectId` values naming a `ref` of
   * the batch are remapped; any other value is dropped (an item without a parent in the batch is read in world
   * coordinates, a connector end keeps its point). `offset` moves the batch's roots and connector points. Invalid items
   * are skipped. Returns the new ids, in order.
   */
  insertObjects(items: { ref: string; data: NewObject }[], opts?: { offset?: Point }): string[];
  /**
   * Groups at least two top-level objects that share a parent: the group takes the top member's place in z, its origin
   * is the members' top-left corner and they keep their world positions. Returns the group id, or '' when refused.
   */
  group(ids: string[]): string;
  /**
   * Dissolves groups and unlocked sections ("Remove section", P5e): their children return to the container's parent at
   * its place in z, world positions kept.
   */
  ungroup(ids: string[]): string[];
  /**
   * What an unfurl found for a link card or an embed (REPAIR_ORIGIN: fetched data, not a user edit, never undone).
   * Fields of the other type are ignored.
   */
  setLinkPreview(id: string, patch: LinkPreviewPatch): void;
  /** Table edits (no-ops on other types); a table keeps 1-100 rows and 1-26 columns, its size follows them. */
  tableInsertRow(id: string, index: number): void;
  tableInsertCol(id: string, index: number): void;
  tableDeleteRow(id: string, rowId: string): void;
  tableDeleteCol(id: string, colId: string): void;
  tableSetCell(id: string, rowId: string, colId: string, text: string): void;
  tableResizeCol(id: string, colId: string, w: number): void;
  /**
   * Task list edits (P16-5): no-ops on other types, on a read-only board and on a list locked itself or by a section
   * around it. A list keeps at most 200 tasks of 1000 characters (title 200), on one line each, indented 0 to 2.
   * `taskAdd` returns the new task's id ('' when refused); `taskMove`'s index counts the other tasks.
   */
  taskAdd(id: string, index: number, text?: string): string;
  taskSetText(id: string, itemId: string, text: string): void;
  taskSetTitle(id: string, text: string): void;
  /** Checking records who and when (the command's author stamp); unchecking clears both. */
  taskSetDone(id: string, itemId: string, done: boolean): void;
  taskDelete(id: string, itemId: string): void;
  taskMove(id: string, itemId: string, index: number): void;
  taskIndent(id: string, itemId: string, indent: number): void;
  taskAssign(id: string, itemId: string, userId: string | null): void;
  /** Non-destructive crop of an image: the box shrinks or grows to show `crop` at the same source scale (one step). */
  setCrop(id: string, crop: Crop): void;
  /**
   * Another picture in an image (P5f): the box keeps its width and centre and takes the new picture's ratio; the crop
   * is reset and the alt text emptied (it described the old picture). One step; a locked image is never replaced.
   */
  replaceImage(id: string, assetId: string, size: { w: number; h: number }): void;
  /** Moves each object by its own world delta (top-level ones only), in one transaction. */
  moveEach(deltas: ReadonlyMap<string, Point>): void;
  /** Aligns the top-level objects (connectors excepted) on their common bounds. */
  align(ids: string[], mode: AlignMode): void;
  /** Equal gaps between at least three top-level objects; the extremes stay. */
  distribute(ids: string[], axis: 'x' | 'y'): void;
  /** Tidies the top-level objects (connectors excepted) in reading order, from the selection's top-left corner. */
  arrange(ids: string[], mode: ArrangeMode, gap: number): void;
  /**
   * Tidies a section's direct, unlocked children from its inner corner (`ARRANGE_MARGIN`) and fits the section around
   * them and its locked children, in one transaction. Nothing to move, a locked section or a read-only board: no write.
   */
  arrangeSection(sectionId: string, mode: ArrangeMode, gap: number): void;
  /** "Fit to content" (P5e): the section wraps its children (`ARRANGE_MARGIN`, at least `SECTION_MIN`), which stay put; one step. */
  fitSectionToContent(sectionId: string): void;
  /** Board settings, merged key by key; shared with peers, not an undo step (the undo manager tracks objects only). */
  setBoardMeta(patch: Partial<BoardMeta>): void;
  /** Orphaned or cyclic `parentId` → null, in one REPAIR_ORIGIN transaction (not undoable). Returns the number of fixes. */
  repairOrphans(): number;
  /**
   * Points every image of asset `from` at `to` (an upload found the same content already stored), in one
   * REPAIR_ORIGIN transaction: undo never brings back an id that no longer exists. Returns the number of images.
   */
  replaceAssetId(from: string, to: string): number;
  /**
   * Pages (P14). Each is one undo step and a no-op when read-only; the first one writes the implicit `main` page and
   * `schemaVersion` 3 (plan Ruling 4). `addPage`: after `after` (default: last), null at PAGE_LIMIT.
   */
  addPage(opts?: { name?: string; after?: string }): string | null;
  renamePage(id: string, name: string): void;
  /** To `index` among the other pages. */
  movePage(id: string, index: number): void;
  /** A deep copy right after `id` (new ids, parents and connector ends remapped), named `name` (default: the same). */
  duplicatePage(id: string, name?: string): string | null;
  /** The page and its objects; false for the last page or an unknown id. */
  deletePage(id: string): boolean;
  /** Objects and their descendants to another page at the same world place, out of a parent left behind, connectors to what stays detached. */
  moveToPage(ids: string[], pageId: string): void;
  /** "Import here" of a file with several pages (Ruling 9): each becomes a new last page with its batch, one step; null past PAGE_LIMIT. */
  /** `ref`: the page's id in the source; a page link to another page of the batch follows it to its new page. */
  insertPages(pages: { ref?: string; name: string; items: { ref: string; data: NewObject }[] }[], opts?: { offset?: Point }): { pageIds: string[]; ids: string[] } | null;
}

/**
 * What `arrangeSection` would do: deltas for the section's direct unlocked children (connectors aside) and its new size,
 * around its locked children too; null when nothing would move. Shared with the live preview.
 */
export function planSectionArrangement(
  g: SceneGraph,
  read: (id: string) => Readonly<ObjectData> | undefined,
  sectionId: string,
  mode: ArrangeMode,
  gap: number,
): { moves: Map<string, Point>; w: number; h: number } | null {
  const box = g.world.get(sectionId);
  if (!box || read(sectionId)?.type !== 'section') return null;
  const kids = (g.children.get(sectionId) ?? []).filter((id) => read(id) && read(id)!.type !== 'connector' && g.world.has(id));
  const items = kids.filter((id) => !read(id)!.locked).map((id) => ({ id, rect: aabb(g.world.get(id)!) }));
  if (!items.length) return null;
  const out = arrangeInside(aabb(box), items, mode, gap, ARRANGE_MARGIN);
  let { maxX, maxY } = out.frame;
  for (const id of kids.filter((k) => read(k)!.locked)) {
    const r = aabb(g.world.get(id)!);
    maxX = Math.max(maxX, r.maxX + ARRANGE_MARGIN);
    maxY = Math.max(maxY, r.maxY + ARRANGE_MARGIN);
  }
  const moves = new Map(items.map((i) => [i.id, { x: out.positions.get(i.id)!.x - i.rect.minX, y: out.positions.get(i.id)!.y - i.rect.minY }]));
  return { moves, w: maxX - out.frame.minX, h: maxY - out.frame.minY };
}

export type CommandContext = {
  doc: Y.Doc;
  objects: Y.Map<Y.Map<unknown>>;
  /** The active page's objects sorted by z, ascending. */
  list: () => ReadonlyArray<Readonly<ObjectData>>;
  /** Every page's objects sorted by z (P14): new objects go above the whole board, asset and repair passes reach all. */
  listAll: () => ReadonlyArray<Readonly<ObjectData>>;
  /** The active page, written on every object this client adds (P14). */
  page: () => string;
  listPage: (pageId: string) => ReadonlyArray<Readonly<ObjectData>>;
  graphOf: (pageId: string) => SceneGraph;
  pageOf: (id: string) => string | undefined;
  read: (id: string) => Readonly<ObjectData> | undefined;
  graph: () => SceneGraph;
  /** Read-only (viewer, revoked editor): every command is a no-op; `createObject` returns '' and `duplicate` []. */
  readOnly: () => boolean;
  /** Called before each command's transaction: outside a gesture, starts a new undo step (no merge with the previous one). */
  step?: () => void;
  /** Who adds objects now and when (P5d); null or absent: local mode, no author recorded. */
  stamp?: () => Authorship | null;
  /** Told when a command is refused because the board is full (P12a); nothing was written. */
  limited?: (kind: LimitKind) => void;
};

export type Authorship = { createdBy: string; createdAt: number };

/** A new object's fields with the author of this action, never one it was copied from (Ruling 7). */
export function withAuthor<T extends object>(data: T, stamp: Authorship | null | undefined): T {
  const rest = { ...data } as Record<string, unknown>;
  delete rest.createdBy;
  delete rest.createdAt;
  return (stamp ? { ...rest, ...stamp } : rest) as T;
}

function assertFinite(...nums: number[]): void {
  for (const n of nums) if (!Number.isFinite(n)) throw new Error('non-finite number rejected');
}

/**
 * Every write to the document goes through here, in ONE transaction with LOCAL_ORIGIN
 * (so a command is exactly one undo step).
 */
export function createCommands(ctx: CommandContext): Commands {
  const tx = (fn: () => void) => {
    ctx.step?.();
    ctx.doc.transact(fn, LOCAL_ORIGIN);
  };
  const num = (m: Y.Map<unknown>, k: string) => m.get(k) as number;
  /** False (and the UI told) when adding these objects would pass a board limit; a batch fits whole or not at all. */
  const fits = (added: ReadonlyArray<{ type?: unknown }>): boolean => {
    const kind = overLimit(boardCounts(ctx.objects), { objects: added.length, images: added.filter((o) => o.type === 'image').length });
    if (kind) ctx.limited?.(kind);
    return !kind;
  };

  const assignBetween = (selectedAsc: ReadonlyArray<Readonly<ObjectData>>, lower: string | null, upper: string | null) => {
    let prev = lower;
    for (const s of selectedAsc) {
      const z = generateKeyBetween(prev, upper);
      ctx.objects.get(s.id)?.set('z', z);
      prev = z;
    }
  };

  /** World position of the local origin `parentId` puts its children at. */
  const parentOrigin = (parentId: string | null): Point => (parentId === null ? { x: 0, y: 0 } : (ctx.graph().origin.get(parentId) ?? { x: 0, y: 0 }));
  const effectiveParent = (id: string) => ctx.graph().parent.get(id) ?? null;
  const isContainer = (type: unknown) => (CONTAINER_TYPES as readonly unknown[]).includes(type);

  /** Runs a table edit in one transaction (nothing for other types or when read-only). */
  const table = (id: string, fn: (m: Y.Map<unknown>, rows: Y.Array<Y.Map<unknown>>, cols: Y.Array<{ id: string; w: number }>) => void) => {
    if (ctx.readOnly()) return;
    const m = ctx.objects.get(id);
    const rows = m?.get('rows');
    const cols = m?.get('cols');
    if (!m || m.get('type') !== 'table' || !(rows instanceof Y.Array) || !(cols instanceof Y.Array)) return;
    tx(() => fn(m, rows as Y.Array<Y.Map<unknown>>, cols as Y.Array<{ id: string; w: number }>));
  };
  /** Runs a task list edit in one transaction (nothing for other types, when read-only or when the list is locked). */
  const tasks = (id: string, fn: (m: Y.Map<unknown>, items: Y.Map<Y.Map<unknown>>) => void) => {
    if (ctx.readOnly()) return;
    const m = ctx.objects.get(id);
    const items = m?.get('items');
    if (!m || m.get('type') !== 'tasklist' || !(items instanceof Y.Map) || isEffectivelyLocked(ctx.graph(), ctx.read, id)) return;
    tx(() => fn(m, items as Y.Map<Y.Map<unknown>>));
  };
  /** One task of a list, or nothing. */
  const taskOf = (items: Y.Map<Y.Map<unknown>>, itemId: string) => {
    const t = items.get(itemId);
    return t instanceof Y.Map ? t : null;
  };
  /** Single-line text within `max` characters (Ruling 4: pasted newlines become spaces). */
  const oneLine = (text: string, max: number) => text.replace(/[\r\n]+/g, ' ').slice(0, max);
  /** Rewrites a Y.Text child of `m` (created when missing), so concurrent typing elsewhere merges. */
  const writeText = (m: Y.Map<unknown>, key: string, text: string) => {
    let t = m.get(key);
    if (!(t instanceof Y.Text)) {
      t = new Y.Text();
      m.set(key, t);
    }
    applyTextDiff(t as Y.Text, text);
  };
  /** Stored height kept in step with the tasks: it notifies list subscribers, and older readers see a sensible size. */
  const syncTaskHeight = (m: Y.Map<unknown>, items: Y.Map<Y.Map<unknown>>) =>
    m.set('h', taskListHeight(Math.min(TASK_MAX_ITEMS, sortedTaskEntries(items).length), m.get('compact') === true));
  /** Moves one object (a connector carries its free ends; its own box stays at the origin). */
  const shift = (m: Y.Map<unknown>, dx: number, dy: number) => {
    if (m.get('type') === 'connector') {
      for (const k of ['start', 'end'] as const) {
        const e = m.get(k) as ConnectorEnd;
        if (e.objectId === null) m.set(k, { ...e, x: e.x + dx, y: e.y + dy });
      }
      return;
    }
    m.set('x', num(m, 'x') + dx);
    m.set('y', num(m, 'y') + dy);
  };
  /** Layout items: top-level objects other than connectors, by their world bounds. */
  const layoutItems = (ids: string[]) => {
    const g = ctx.graph();
    return topLevel(g, ids)
      .filter((id) => ctx.read(id) && ctx.read(id)!.type !== 'connector' && g.world.has(id))
      .map((id) => ({ id, rect: aabb(g.world.get(id)!) }));
  };

  const selectionAsc = (ids: string[]) => {
    const set = new Set(ids);
    return ctx.list().filter((o) => set.has(o.id));
  };

  const pagesMap = () => ctx.doc.getMap<unknown>('pages');
  const pageMap = (id: string) => {
    const m = pagesMap().get(id);
    return m instanceof Y.Map ? (m as Y.Map<unknown>) : null;
  };
  /** Inside a transaction: the implicit `main` page becomes a real entry and the document says it has pages. */
  const materializePages = () => {
    const pages = readPages(ctx.doc);
    if (pages.length === 1 && pages[0]!.id === PAGE_MAIN && !pageMap(PAGE_MAIN)) {
      const m = new Y.Map<unknown>();
      pagesMap().set(PAGE_MAIN, m);
      m.set('name', '');
      m.set('order', pages[0]!.order);
    }
    const meta = ctx.doc.getMap('meta');
    const v = meta.get('schemaVersion');
    if (typeof v !== 'number' || v < SCHEMA_VERSION) meta.set('schemaVersion', SCHEMA_VERSION);
  };
  /** An order key between the page at `index - 1` and the next page strictly above it (ties never break the key generator). */
  const orderAt = (others: readonly { order: string }[], index: number) => {
    const lower = index > 0 ? others[index - 1]!.order : null;
    const upper = others.slice(index).find((p) => lower === null || p.order > lower)?.order ?? null;
    return generateKeyBetween(lower, upper);
  };
  /** New ids for a pasted batch (parents and connector ends follow), offset roots, invalid objects left out. */
  const prepareInsert = (items: { ref: string; data: NewObject }[], offset?: Point) => {
    const off = offset ?? { x: 0, y: 0 };
    assertFinite(off.x, off.y);
    const refs = new Map<string, string>();
    for (const it of items) if (!refs.has(it.ref)) refs.set(it.ref, newId());
    const mapEnd = (e: ConnectorEnd): ConnectorEnd => {
      const objectId = e.objectId !== null && refs.has(e.objectId) ? refs.get(e.objectId)! : null;
      return { objectId, anchor: objectId ? e.anchor : null, x: e.x + off.x, y: e.y + off.y };
    };
    const out: { id: string; data: Record<string, unknown> }[] = [];
    for (const it of items) {
      const d = structuredClone(it.data) as NewObject;
      const inBatch = d.parentId !== null && refs.has(d.parentId) && items.some((x) => x.ref === d.parentId);
      let data: Record<string, unknown> = { ...d, parentId: inBatch ? refs.get(d.parentId!)! : null };
      if (!inBatch) data = { ...data, x: d.x + off.x, y: d.y + off.y };
      if (d.type === 'connector') data = { ...data, parentId: null, x: 0, y: 0, start: mapEnd(d.start), end: mapEnd(d.end) };
      if (!parseObject({ ...data, id: 'probe', z: 'a0' })) continue;
      out.push({ id: refs.get(it.ref)!, data });
    }
    return out;
  };
  /** Inside a transaction: writes a prepared batch on `pageId`, above `z.prev` (advanced as it goes). */
  const writeInsert = (out: { id: string; data: Record<string, unknown> }[], pageId: string, z: { prev: string | null }) => {
    const valid = new Set(out.map((o) => o.id));
    for (const o of out) {
      z.prev = generateKeyBetween(z.prev, null);
      // A parent that was skipped as invalid leaves its child at the root, where it lands in world coordinates.
      const pid = o.data.parentId as string | null;
      const data = pid !== null && !valid.has(pid) ? { ...o.data, parentId: null } : o.data;
      const m = new Y.Map<unknown>();
      ctx.objects.set(o.id, m);
      writeObject(m, withAuthor({ ...data, id: o.id, z: z.prev, pageId }, ctx.stamp?.()));
    }
  };
  const newPage = (name: string, order: string, id = newId()) => {
    const m = new Y.Map<unknown>();
    pagesMap().set(id, m);
    m.set('name', name.slice(0, PAGE_NAME_MAX));
    m.set('order', order);
    return id;
  };

  return {
    createObject(data, opts) {
      if (ctx.readOnly()) return '';
      if (!parseObject({ ...data, id: 'probe', z: 'a0' })) throw new Error('invalid object rejected');
      if (!fits([data])) return '';
      const list = ctx.list();
      let lower: string | null = ctx.listAll().at(-1)?.z ?? null;
      let upper: string | null = null;
      const above = opts?.above ? ctx.read(opts.above) : undefined;
      if (above) {
        lower = above.z;
        upper = list.find((o) => o.z > above.z)?.z ?? null;
      }
      const id = newId();
      const z = generateKeyBetween(lower, upper);
      tx(() => {
        const m = new Y.Map<unknown>();
        ctx.objects.set(id, m);
        writeObject(m, withAuthor({ ...data, id, z, pageId: ctx.page() }, ctx.stamp?.()));
      });
      return id;
    },

    moveObjects(ids, dx, dy) {
      if (ctx.readOnly()) return;
      assertFinite(dx, dy);
      const top = topLevel(ctx.graph(), ids);
      tx(() => {
        for (const id of top) {
          const m = ctx.objects.get(id);
          if (m) shift(m, dx, dy);
        }
      });
    },

    resizeObject(id, box) {
      if (ctx.readOnly()) return;
      assertFinite(box.x, box.y, box.w, box.h);
      tx(() => {
        const m = ctx.objects.get(id);
        if (!m || m.get('type') === 'connector') return; // drawn from its ends
        const w = Math.max(1, box.w);
        const h = Math.max(1, box.h);
        const o = parentOrigin(effectiveParent(id));
        if (m.get('type') === 'arrow') {
          // Arrow points are relative to (x, y): they follow the box.
          const sx = w / Math.max(1, num(m, 'w'));
          const sy = h / Math.max(1, num(m, 'h'));
          m.set('points', (m.get('points') as Point[]).map((p) => ({ x: p.x * sx, y: p.y * sy })));
        }
        // A section's children are stored relative to it: they stay where they are in the world (P5e).
        if (m.get('type') === 'section') {
          const dx = box.x - o.x - num(m, 'x');
          const dy = box.y - o.y - num(m, 'y');
          if (dx || dy)
            for (const cid of ctx.graph().children.get(id) ?? []) {
              const c = ctx.objects.get(cid);
              if (!c) continue;
              c.set('x', num(c, 'x') - dx);
              c.set('y', num(c, 'y') - dy);
            }
        }
        m.set('x', box.x - o.x);
        m.set('y', box.y - o.y);
        m.set('w', w);
        m.set('h', h);
      });
    },

    rotateObjects(ids, deltaDeg, pivot) {
      if (ctx.readOnly()) return;
      assertFinite(deltaDeg);
      if (pivot) assertFinite(pivot.x, pivot.y);
      const c = Math.cos((deltaDeg * Math.PI) / 180);
      const s = Math.sin((deltaDeg * Math.PI) / 180);
      tx(() => {
        for (const id of ids) {
          const m = ctx.objects.get(id);
          // Task lists keep their rows level (P16-5 Ruling 4), like containers.
          if (!m || isContainer(m.get('type')) || m.get('type') === 'connector' || m.get('type') === 'tasklist') continue;
          if (pivot) {
            const o = parentOrigin(effectiveParent(id));
            const cx = o.x + num(m, 'x') + num(m, 'w') / 2;
            const cy = o.y + num(m, 'y') + num(m, 'h') / 2;
            const dx = cx - pivot.x;
            const dy = cy - pivot.y;
            m.set('x', pivot.x + dx * c - dy * s - num(m, 'w') / 2 - o.x);
            m.set('y', pivot.y + dx * s + dy * c - num(m, 'h') / 2 - o.y);
          }
          m.set('rotation', normalizeAngle(num(m, 'rotation') + deltaDeg));
        }
      });
    },

    pasteStyle(ids, clip) {
      if (ctx.readOnly()) return;
      tx(() => {
        for (const id of ids) {
          const m = ctx.objects.get(id);
          if (!m) continue;
          // stylePatch only reads the type.
          const { props, style } = stylePatch(clip, { type: m.get('type'), style: {} } as unknown as ObjectData);
          for (const [k, v] of Object.entries(props)) if (k !== 'fontSize' || (typeof v === 'number' && Number.isFinite(v))) m.set(k, v);
          if (Object.keys(style).length) m.set('style', { ...((m.get('style') as Record<string, unknown>) ?? {}), ...style });
        }
      });
    },

    setStyle(ids, patch) {
      if (ctx.readOnly()) return;
      tx(() => {
        for (const id of ids) {
          const m = ctx.objects.get(id);
          if (!m) continue;
          m.set('style', { ...((m.get('style') as Record<string, unknown>) ?? {}), ...patch });
        }
      });
    },

    setProps(ids, patch) {
      if (ctx.readOnly()) return;
      if (patch.fontSize !== undefined) assertFinite(patch.fontSize);
      tx(() => {
        for (const id of ids) {
          const m = ctx.objects.get(id);
          if (!m) continue;
          const type = m.get('type');
          if (patch.locked !== undefined) m.set('locked', patch.locked);
          if (patch.color !== undefined && type === 'sticky') m.set('color', patch.color);
          if (patch.fontSize !== undefined && type === 'text') m.set('fontSize', patch.fontSize);
          if (patch.shape !== undefined && type === 'shape' && SHAPES.includes(patch.shape)) m.set('shape', patch.shape);
          if (type === 'connector') {
            if (patch.routing !== undefined && ROUTINGS.includes(patch.routing)) m.set('routing', patch.routing);
            if (patch.startCap !== undefined && CAPS.includes(patch.startCap)) m.set('startCap', patch.startCap);
            if (patch.endCap !== undefined && CAPS.includes(patch.endCap)) m.set('endCap', patch.endCap);
            if (patch.bend !== undefined && Number.isFinite(patch.bend)) m.set('bend', Math.max(-2, Math.min(2, patch.bend)));
            if (patch.label !== undefined) m.set('label', patch.label.slice(0, 200));
          }
          if (patch.alt !== undefined && type === 'image') m.set('alt', patch.alt.slice(0, 500));
          if (patch.compact !== undefined && type === 'tasklist') m.set('compact', patch.compact);
          if (patch.targetPageId !== undefined && type === 'pagelink' && patch.targetPageId.length >= 1 && patch.targetPageId.length <= 64) m.set('targetPageId', patch.targetPageId);
          if (type === 'section') {
            if (patch.name !== undefined) m.set('name', patch.name.slice(0, 200));
            if (patch.fill !== undefined) m.set('fill', patch.fill);
            if (patch.clip !== undefined) m.set('clip', patch.clip);
            if (patch.collapsed !== undefined) m.set('collapsed', patch.collapsed);
          }
        }
      });
    },

    setText(id, text) {
      if (ctx.readOnly()) return;
      const m = ctx.objects.get(id);
      if (!m || !isTextType(m.get('type'))) return;
      tx(() => {
        // Same fragment, new content: an editor bound to it stays bound.
        let f = m.get('content');
        if (!(f instanceof Y.XmlFragment)) {
          f = new Y.XmlFragment();
          m.set('content', f);
          m.delete('text');
        } else (f as Y.XmlFragment).delete(0, (f as Y.XmlFragment).length);
        fillFragment(f as Y.XmlFragment, plainToRich(text));
      });
    },

    ensureContent(id) {
      if (ctx.readOnly()) return null;
      const m = ctx.objects.get(id);
      if (!m || !isTextType(m.get('type'))) return null;
      const existing = m.get('content');
      if (existing instanceof Y.XmlFragment) return existing;
      const f = new Y.XmlFragment();
      ctx.doc.transact(() => {
        const text = m.get('text');
        m.set('content', f);
        m.delete('text');
        fillFragment(f, plainToRich(typeof text === 'string' ? text : ''));
      }, REPAIR_ORIGIN);
      return f;
    },

    reorder(ids, where) {
      if (ctx.readOnly()) return;
      const selected = selectionAsc(ids);
      if (selected.length === 0) return;
      const sel = new Set(selected.map((o) => o.id));
      const others = ctx.list().filter((o) => !sel.has(o.id));
      tx(() => {
        if (where === 'front') return assignBetween(selected, others.at(-1)?.z ?? null, null);
        if (where === 'back') return assignBetween(selected, null, others[0]?.z ?? null);
        // Neighbours on the same key (two peers created them at once) count as one step: the bound is the next key
        // strictly beyond, or the key generator throws.
        if (where === 'forward') {
          const topZ = selected.at(-1)!.z;
          const next = others.find((o) => o.z > topZ);
          if (!next) return;
          return assignBetween(selected, next.z, others.find((o) => o.z > next.z)?.z ?? null);
        }
        const lowZ = selected[0]!.z;
        const prev = others.findLast((o) => o.z < lowZ);
        if (!prev) return;
        assignBetween(selected, others.findLast((o) => o.z < prev.z)?.z ?? null, prev.z);
      });
    },

    duplicate(ids, offset) {
      if (ctx.readOnly()) return [];
      assertFinite(offset.x, offset.y);
      const g = ctx.graph();
      const top = topLevel(g, ids);
      const selected = selectionAsc([...top, ...top.flatMap((id) => descendantsOf(g, id))]);
      if (!fits(selected)) return [];
      const idMap = new Map(selected.map((o) => [o.id, newId()]));
      let prev: string | null = ctx.listAll().at(-1)?.z ?? null;
      tx(() => {
        for (const o of selected) {
          const id = idMap.get(o.id)!;
          const z = generateKeyBetween(prev, null);
          prev = z;
          // Descendants keep their coordinates: they are relative to the (copied) parent.
          const root = !(o.parentId !== null && idMap.has(o.parentId));
          const copy = {
            ...structuredClone(o),
            id,
            z,
            pageId: ctx.page(),
            x: o.x + (root ? offset.x : 0),
            y: o.y + (root ? offset.y : 0),
            parentId: root ? o.parentId : idMap.get(o.parentId!)!,
          };
          if (copy.type === 'connector') {
            copy.x = 0; // drawn from its ends
            copy.y = 0;
            const remap = (e: ConnectorEnd): ConnectorEnd => (e.objectId !== null && idMap.has(e.objectId) ? { ...e, objectId: idMap.get(e.objectId)! } : e);
            const shifted = (e: ConnectorEnd): ConnectorEnd => (e.objectId === null ? { ...e, x: e.x + offset.x, y: e.y + offset.y } : e);
            copy.start = shifted(remap(copy.start));
            copy.end = shifted(remap(copy.end));
          }
          const m = new Y.Map<unknown>();
          ctx.objects.set(id, m);
          writeObject(m, withAuthor(copy, ctx.stamp?.()));
        }
      });
      return top.filter((id) => idMap.has(id)).map((id) => idMap.get(id)!);
    },

    deleteObjects(ids) {
      if (ctx.readOnly()) return;
      const g = ctx.graph();
      const all = new Set(ids.flatMap((id) => [id, ...descendantsOf(g, id)]));
      // Locked descendants (and what they hold) survive their container: they move up to the closest survivor.
      const spared = new Set<string>();
      for (const id of all)
        if (!ids.includes(id) && ctx.read(id)?.locked) for (const k of [id, ...descendantsOf(g, id)]) spared.add(k);
      for (const k of spared) all.delete(k);
      // A group left without children goes too (it would stay as an invisible, clickable box).
      for (let changed = true; changed; ) {
        changed = false;
        for (const o of ctx.list()) {
          const kids = g.children.get(o.id) ?? [];
          if (o.type === 'group' && !all.has(o.id) && kids.length > 0 && kids.every((k) => all.has(k))) {
            all.add(o.id);
            changed = true;
          }
        }
      }
      const rehome = [...spared].filter((id) => all.has(g.parent.get(id) ?? ''));
      tx(() => {
        for (const id of rehome) {
          let p = g.parent.get(id) ?? null;
          while (p !== null && all.has(p)) p = g.parent.get(p) ?? null;
          const at = g.origin.get(id)!;
          const base = parentOrigin(p);
          const m = ctx.objects.get(id)!;
          m.set('parentId', p);
          m.set('pageId', ctx.page());
          m.set('x', at.x - base.x);
          m.set('y', at.y - base.y);
        }
        for (const o of ctx.list()) {
          if (o.type !== 'connector' || all.has(o.id)) continue;
          for (const k of ['start', 'end'] as const) {
            const e = o[k];
            if (e.objectId === null || !all.has(e.objectId)) continue;
            const { p } = resolveEnd(e, g.world, g.hidden, g.parent);
            ctx.objects.get(o.id)?.set(k, { objectId: null, anchor: null, x: p.x, y: p.y });
          }
        }
        for (const id of all) ctx.objects.delete(id);
      });
    },

    setConnectorEnd(id, which, end) {
      if (ctx.readOnly()) return;
      assertFinite(end.x, end.y);
      const m = ctx.objects.get(id);
      if (!m || m.get('type') !== 'connector' || end.objectId === id) return;
      tx(() => m.set(which, { objectId: end.objectId, anchor: end.objectId ? end.anchor : null, x: end.x, y: end.y }));
    },

    reparent(ids, parentId) {
      if (ctx.readOnly()) return;
      const g = ctx.graph();
      if (parentId !== null && !isContainer(ctx.read(parentId)?.type)) return;
      const target = parentOrigin(parentId);
      const moving = selectionAsc(topLevel(g, ids)).filter(
        (o) => o.type !== 'connector' && o.id !== parentId && (parentId === null || !descendantsOf(g, o.id).includes(parentId)) && effectiveParent(o.id) !== parentId,
      );
      if (moving.length === 0) return;
      const siblings = ctx.list().filter((o) => effectiveParent(o.id) === parentId);
      let prev: string | null = siblings.at(-1)?.z ?? null;
      tx(() => {
        for (const o of moving) {
          const m = ctx.objects.get(o.id);
          const at = g.origin.get(o.id);
          if (!m || !at) continue;
          const z = generateKeyBetween(prev, null);
          prev = z;
          m.set('parentId', parentId);
          m.set('pageId', ctx.page()); // the page it is seen on, even if its own field was stale (Ruling 5)
          m.set('x', at.x - target.x);
          m.set('y', at.y - target.y);
          m.set('z', z);
        }
      });
    },

    insertObjects(items, opts) {
      if (ctx.readOnly()) return [];
      const batch = prepareInsert(items, opts?.offset);
      if (!fits(batch.map((o) => o.data))) return [];
      const z = { prev: ctx.listAll().at(-1)?.z ?? null };
      tx(() => writeInsert(batch, ctx.page(), z));
      return batch.map((o) => o.id);
    },

    insertPages(pages, opts) {
      if (ctx.readOnly()) return null;
      const existing = readPages(ctx.doc);
      if (existing.length + pages.length > PAGE_LIMIT) return null;
      const batches = pages.map((p) => prepareInsert(p.items, opts?.offset));
      if (!fits(batches.flat().map((o) => o.data))) return null;
      const newIds = pages.map(() => newId());
      const byRef = new Map(pages.flatMap((p, i) => (p.ref ? [[p.ref, newIds[i]!] as const] : [])));
      for (const batch of batches)
        for (const o of batch) if (o.data.type === 'pagelink' && byRef.has(o.data.targetPageId as string)) o.data = { ...o.data, targetPageId: byRef.get(o.data.targetPageId as string)! };
      const pageIds: string[] = [];
      const z = { prev: ctx.listAll().at(-1)?.z ?? null };
      tx(() => {
        materializePages();
        let after = existing.at(-1)!.order;
        pages.forEach((p, i) => {
          after = generateKeyBetween(after, null);
          const pageId = newPage(p.name, after, newIds[i]);
          pageIds.push(pageId);
          writeInsert(batches[i]!, pageId, z);
        });
      });
      return { pageIds, ids: batches.flat().map((o) => o.id) };
    },

    group(ids) {
      if (ctx.readOnly()) return '';
      const g = ctx.graph();
      const members = selectionAsc(topLevel(g, ids)).filter((o) => o.type !== 'connector');
      if (members.length < 2) return '';
      const parentId = effectiveParent(members[0]!.id);
      if (members.some((o) => effectiveParent(o.id) !== parentId)) return '';
      if (!fits([{ type: 'group' }])) return '';
      const origin = parentOrigin(parentId);
      const rects = members.map((o) => aabb(g.world.get(o.id)!));
      const minX = Math.min(...rects.map((r) => r.minX)) - origin.x;
      const minY = Math.min(...rects.map((r) => r.minY)) - origin.y;
      const maxX = Math.max(...rects.map((r) => r.maxX)) - origin.x;
      const maxY = Math.max(...rects.map((r) => r.maxY)) - origin.y;
      const topZ = members.at(-1)!.z;
      const z = generateKeyBetween(topZ, ctx.list().find((o) => o.z > topZ)?.z ?? null);
      const id = newId();
      tx(() => {
        const m = new Y.Map<unknown>();
        ctx.objects.set(id, m);
        writeObject(m, withAuthor({ id, type: 'group', parentId, x: minX, y: minY, w: Math.max(1, maxX - minX), h: Math.max(1, maxY - minY), rotation: 0, z, locked: false, style: {}, pageId: ctx.page() }, ctx.stamp?.()));
        for (const o of members) {
          const mm = ctx.objects.get(o.id)!;
          mm.set('parentId', id);
          mm.set('x', o.x - minX);
          mm.set('y', o.y - minY);
        }
      });
      return id;
    },

    ungroup(ids) {
      if (ctx.readOnly()) return [];
      const g = ctx.graph();
      // A section dissolves the same way ("Remove section", P5e), unless it is locked.
      const groups = ids
        .map((id) => ctx.read(id))
        .filter((o): o is Readonly<ObjectData> => o?.type === 'group' || (o?.type === 'section' && !isEffectivelyLocked(g, ctx.read, o.id)));
      const out: string[] = [];
      tx(() => {
        for (const grp of groups) {
          const parentId = effectiveParent(grp.id);
          const upper = ctx.list().find((o) => o.z > grp.z && effectiveParent(o.id) === parentId)?.z ?? null;
          let prev: string | null = grp.z;
          for (const cid of g.children.get(grp.id) ?? []) {
            const c = ctx.read(cid);
            const m = ctx.objects.get(cid);
            if (!c || !m) continue;
            const z = generateKeyBetween(prev, upper);
            prev = z;
            m.set('parentId', parentId);
            m.set('pageId', ctx.page());
            m.set('x', c.x + grp.x);
            m.set('y', c.y + grp.y);
            m.set('z', z);
            out.push(cid);
          }
          ctx.objects.delete(grp.id);
        }
      });
      return out;
    },

    tableInsertRow(id, index) {
      table(id, (m, rows, cols) => {
        if (rows.length >= TABLE_MAX_ROWS) return;
        const row = new Y.Map<unknown>();
        rows.insert(Math.min(Math.max(0, index), rows.length), [row]);
        row.set('id', newId());
        for (const c of cols.toArray()) row.set(c.id, new Y.Text(''));
        m.set('h', rows.length * TABLE_ROW_H);
      });
    },

    tableInsertCol(id, index) {
      table(id, (m, rows, cols) => {
        if (cols.length >= TABLE_MAX_COLS) return;
        const col = { id: newId(), w: TABLE_COL_W };
        cols.insert(Math.min(Math.max(0, index), cols.length), [col]);
        for (const r of rows.toArray()) r.set(col.id, new Y.Text(''));
        m.set('w', cols.toArray().reduce((s, c) => s + c.w, 0));
      });
    },

    tableDeleteRow(id, rowId) {
      table(id, (m, rows) => {
        const i = rows.toArray().findIndex((r) => r.get('id') === rowId);
        if (i < 0 || rows.length <= 1) return;
        rows.delete(i, 1);
        m.set('h', rows.length * TABLE_ROW_H);
      });
    },

    tableDeleteCol(id, colId) {
      table(id, (m, rows, cols) => {
        const i = cols.toArray().findIndex((c) => c.id === colId);
        if (i < 0 || cols.length <= 1) return;
        cols.delete(i, 1);
        for (const r of rows.toArray()) r.delete(colId);
        m.set('w', cols.toArray().reduce((s, c) => s + c.w, 0));
      });
    },

    tableSetCell(id, rowId, colId, text) {
      table(id, (_m, rows, cols) => {
        const row = rows.toArray().find((r) => r.get('id') === rowId);
        if (!row || !cols.toArray().some((c) => c.id === colId)) return;
        let t = row.get(colId);
        if (!(t instanceof Y.Text)) {
          t = new Y.Text();
          row.set(colId, t);
        }
        applyTextDiff(t as Y.Text, text);
      });
    },

    tableResizeCol(id, colId, w) {
      assertFinite(w);
      table(id, (m, _rows, cols) => {
        const i = cols.toArray().findIndex((c) => c.id === colId);
        if (i < 0) return;
        const width = Math.min(TABLE_MAX_COL_W, Math.max(TABLE_MIN_COL_W, Math.round(w)));
        cols.delete(i, 1);
        cols.insert(i, [{ id: colId, w: width }]);
        m.set('w', cols.toArray().reduce((s, c) => s + c.w, 0));
      });
    },

    taskAdd(id, index, text = '') {
      let out = '';
      tasks(id, (m, items) => {
        let entries = sortedTaskEntries(items);
        if (entries.length >= TASK_MAX_ITEMS) return;
        if (repairTaskOrder(entries)) entries = sortedTaskEntries(items);
        const item = new Y.Map<unknown>();
        out = newId();
        items.set(out, item);
        item.set('pos', posAt(entries, index));
        item.set('text', new Y.Text(oneLine(text, TASK_MAX_TEXT)));
        item.set('done', false);
        item.set('indent', 0);
        item.set('assignee', null);
        syncTaskHeight(m, items);
      });
      return out;
    },

    taskSetText(id, itemId, text) {
      tasks(id, (_m, items) => {
        const t = taskOf(items, itemId);
        if (t) writeText(t, 'text', oneLine(text, TASK_MAX_TEXT));
      });
    },

    taskSetTitle(id, text) {
      tasks(id, (m) => writeText(m, 'title', oneLine(text, TASK_MAX_TITLE)));
    },

    taskSetDone(id, itemId, done) {
      tasks(id, (_m, items) => {
        const t = taskOf(items, itemId);
        if (!t) return;
        t.set('done', done);
        const stamp = done ? ctx.stamp?.() : null;
        if (stamp) {
          t.set('doneBy', stamp.createdBy);
          t.set('doneAt', stamp.createdAt);
        } else {
          t.delete('doneBy');
          t.delete('doneAt');
        }
      });
    },

    taskDelete(id, itemId) {
      tasks(id, (m, items) => {
        if (!taskOf(items, itemId)) return;
        items.delete(itemId);
        syncTaskHeight(m, items);
      });
    },

    taskMove(id, itemId, index) {
      assertFinite(index);
      tasks(id, (_m, items) => {
        const t = taskOf(items, itemId);
        if (!t) return;
        repairTaskOrder(sortedTaskEntries(items));
        const others = sortedTaskEntries(items).filter(([k]) => k !== itemId);
        t.set('pos', posAt(others, index));
      });
    },

    taskIndent(id, itemId, indent) {
      assertFinite(indent);
      tasks(id, (_m, items) => taskOf(items, itemId)?.set('indent', Math.min(TASK_MAX_INDENT, Math.max(0, Math.round(indent)))));
    },

    taskAssign(id, itemId, userId) {
      tasks(id, (_m, items) => taskOf(items, itemId)?.set('assignee', userId));
    },

    setLinkPreview(id, patch) {
      if (ctx.readOnly()) return;
      const m = ctx.objects.get(id);
      const type = m?.get('type');
      if (!m || (type !== 'link' && type !== 'embed')) return;
      const allowed = type === 'link' ? ['title', 'description', 'siteName', 'status', 'thumbAssetId'] : ['title', 'embeddable', 'thumbAssetId'];
      const caps: Record<string, number> = { title: 300, description: 500, siteName: 100 };
      ctx.doc.transact(() => {
        for (const [k, v] of Object.entries(patch)) {
          if (!allowed.includes(k) || v === undefined) continue;
          m.set(k, typeof v === 'string' && caps[k] ? v.slice(0, caps[k]) : v);
        }
      }, REPAIR_ORIGIN);
    },

    setCrop(id, crop) {
      if (ctx.readOnly()) return;
      const o = ctx.read(id);
      const m = ctx.objects.get(id);
      if (!o || !m || o.type !== 'image' || o.locked) return;
      const to = clampCrop(crop);
      const box = cropBox({ x: o.x, y: o.y, w: o.w, h: o.h }, o.crop, to); // local coordinates in, local out
      tx(() => {
        m.set('crop', to);
        m.set('x', box.x);
        m.set('y', box.y);
        m.set('w', Math.max(1, box.w));
        m.set('h', Math.max(1, box.h));
      });
    },

    replaceImage(id, assetId, size) {
      if (ctx.readOnly()) return;
      const o = ctx.read(id);
      const m = ctx.objects.get(id);
      if (!o || !m || o.type !== 'image' || o.locked || !(size.w > 0 && size.h > 0)) return;
      const h = Math.max(1, (o.w * size.h) / size.w);
      tx(() => {
        m.set('assetId', assetId);
        m.set('crop', { x: 0, y: 0, w: 1, h: 1 });
        m.set('alt', '');
        m.set('y', o.y + (o.h - h) / 2);
        m.set('h', h);
      });
    },

    moveEach(deltas) {
      if (ctx.readOnly()) return;
      const top = topLevel(ctx.graph(), [...deltas.keys()]);
      for (const id of top) assertFinite(deltas.get(id)!.x, deltas.get(id)!.y);
      tx(() => {
        for (const id of top) {
          const m = ctx.objects.get(id);
          const d = deltas.get(id)!;
          if (m && (d.x || d.y)) shift(m, d.x, d.y);
        }
      });
    },

    align(ids, mode) {
      this.moveEach(alignDeltas(layoutItems(ids), mode));
    },

    distribute(ids, axis) {
      this.moveEach(distributeDeltas(layoutItems(ids), axis));
    },

    arrange(ids, mode, gap) {
      assertFinite(gap);
      const items = layoutItems(ids);
      const pos = arrangeLayout(items, mode, gap);
      this.moveEach(new Map(items.map((i) => [i.id, { x: pos.get(i.id)!.x - i.rect.minX, y: pos.get(i.id)!.y - i.rect.minY }])));
    },

    arrangeSection(sectionId, mode, gap) {
      assertFinite(gap);
      const section = ctx.read(sectionId);
      if (ctx.readOnly() || section?.type !== 'section' || section.locked) return;
      const plan = planSectionArrangement(ctx.graph(), ctx.read, sectionId, mode, gap);
      if (!plan) return;
      tx(() => {
        this.moveEach(plan.moves);
        const m = ctx.objects.get(sectionId)!;
        m.set('w', plan.w);
        m.set('h', plan.h);
      });
    },

    fitSectionToContent(sectionId) {
      if (ctx.readOnly() || ctx.read(sectionId)?.type !== 'section') return;
      const g = ctx.graph();
      if (isEffectivelyLocked(g, ctx.read, sectionId)) return;
      const kids = (g.children.get(sectionId) ?? []).filter((id) => ctx.read(id)?.type !== 'connector' && g.world.has(id));
      const box = fitSectionBox(kids.map((id) => aabb(g.world.get(id)!)));
      if (box) this.resizeObject(sectionId, box);
    },

    setBoardMeta(patch) {
      if (ctx.readOnly() || !isBoardMetaPatch(patch)) return;
      const meta = ctx.doc.getMap('meta');
      tx(() => {
        if (patch.background) meta.set('background', { ...patch.background });
        if (patch.grid) meta.set('grid', { ...patch.grid });
      });
    },

    repairOrphans() {
      if (ctx.readOnly()) return 0;
      const input = new Map(ctx.listAll().map((o) => [o.id, o as ObjectData]));
      const changes = [...normalizeObjects(input).objects].filter(([id, o]) => o.parentId !== input.get(id)!.parentId);
      if (changes.length === 0) return 0;
      ctx.doc.transact(() => {
        for (const [id, o] of changes) ctx.objects.get(id)?.set('parentId', o.parentId);
      }, REPAIR_ORIGIN);
      return changes.length;
    },

    addPage(opts) {
      if (ctx.readOnly()) return null;
      const pages = readPages(ctx.doc);
      if (pages.length >= PAGE_LIMIT) return null;
      const at = opts?.after ? pages.findIndex((p) => p.id === opts.after) : -1;
      let id = '';
      tx(() => {
        materializePages();
        id = newPage(opts?.name ?? '', orderAt(pages, at >= 0 ? at + 1 : pages.length));
      });
      return id;
    },

    renamePage(id, name) {
      if (ctx.readOnly() || !readPages(ctx.doc).some((p) => p.id === id)) return;
      tx(() => {
        materializePages();
        pageMap(id)?.set('name', name.slice(0, PAGE_NAME_MAX));
      });
    },

    movePage(id, index) {
      if (ctx.readOnly()) return;
      const pages = readPages(ctx.doc);
      const from = pages.findIndex((p) => p.id === id);
      if (from < 0 || pages.length < 2) return;
      const others = pages.filter((p) => p.id !== id);
      const to = Math.max(0, Math.min(others.length, Math.trunc(index)));
      if (to === from) return;
      tx(() => {
        materializePages();
        pageMap(id)?.set('order', orderAt(others, to));
      });
    },

    duplicatePage(id, name) {
      if (ctx.readOnly()) return null;
      const pages = readPages(ctx.doc);
      const at = pages.findIndex((p) => p.id === id);
      if (at < 0 || pages.length >= PAGE_LIMIT) return null;
      const source = ctx.listPage(id);
      if (!fits(source)) return null;
      const idMap = new Map(source.map((o) => [o.id, newId()]));
      let pageId = '';
      let prev: string | null = ctx.listAll().at(-1)?.z ?? null;
      tx(() => {
        materializePages();
        pageId = newPage(name ?? pages[at]!.name, orderAt(pages, at + 1));
        for (const o of source) {
          const z = generateKeyBetween(prev, null);
          prev = z;
          const copy = { ...structuredClone(o), id: idMap.get(o.id)!, z, pageId, parentId: o.parentId === null ? null : (idMap.get(o.parentId) ?? null) };
          if (copy.type === 'pagelink' && copy.targetPageId === id) copy.targetPageId = pageId; // a link to its own page follows the copy
          if (copy.type === 'connector') {
            const remap = (e: ConnectorEnd): ConnectorEnd =>
              e.objectId !== null && idMap.has(e.objectId) ? { ...e, objectId: idMap.get(e.objectId)! } : { objectId: null, anchor: null, x: e.x, y: e.y };
            copy.start = remap(copy.start);
            copy.end = remap(copy.end);
          }
          const m = new Y.Map<unknown>();
          ctx.objects.set(copy.id, m);
          writeObject(m, withAuthor(copy, ctx.stamp?.()) as Record<string, unknown>);
        }
      });
      return pageId;
    },

    deletePage(id) {
      if (ctx.readOnly()) return false;
      const pages = readPages(ctx.doc);
      if (pages.length < 2 || !pages.some((p) => p.id === id)) return false;
      const doomed = ctx.listPage(id).map((o) => o.id);
      tx(() => {
        materializePages();
        for (const oid of doomed) ctx.objects.delete(oid);
        pagesMap().delete(id);
      });
      return true;
    },

    moveToPage(ids, pageId) {
      if (ctx.readOnly() || !readPages(ctx.doc).some((p) => p.id === pageId)) return;
      const from = ids.map((i) => ctx.pageOf(i)).find((p) => p !== undefined);
      if (from === undefined || from === pageId) return;
      const g = ctx.graphOf(from);
      const top = topLevel(g, ids.filter((i) => ctx.pageOf(i) === from));
      if (top.length === 0) return;
      const moving = new Set(top.flatMap((i) => [i, ...descendantsOf(g, i)]));
      tx(() => {
        materializePages();
        for (const oid of moving) ctx.objects.get(oid)?.set('pageId', pageId);
        for (const oid of top) {
          const m = ctx.objects.get(oid);
          const at = g.origin.get(oid);
          if (!m || !at || m.get('parentId') === null) continue;
          // Out of a parent that stays behind, at the same world place.
          m.set('parentId', null);
          m.set('x', at.x);
          m.set('y', at.y);
        }
        // A connector never spans two pages: the end on the other side becomes free, where it was drawn.
        for (const o of ctx.listPage(from)) {
          if (o.type !== 'connector') continue;
          for (const k of ['start', 'end'] as const) {
            const e = o[k];
            if (e.objectId === null || moving.has(e.objectId) === moving.has(o.id)) continue;
            const { p } = resolveEnd(e, g.world, g.hidden, g.parent);
            ctx.objects.get(o.id)?.set(k, { objectId: null, anchor: null, x: p.x, y: p.y });
          }
        }
      });
    },

    replaceAssetId(from, to) {
      if (ctx.readOnly() || from === to) return 0;
      const ids = ctx.listAll().filter((o) => o.type === 'image' && o.assetId === from).map((o) => o.id);
      if (ids.length === 0) return 0;
      ctx.doc.transact(() => {
        for (const id of ids) ctx.objects.get(id)?.set('assetId', to);
      }, REPAIR_ORIGIN);
      return ids.length;
    },
  };
}
